

import PropTypes from 'prop-types';
import { Spinner, Form, Row, Col, InputGroup, Button } from 'react-bootstrap';

// Formatear monto con separadores de miles
const formatMonto = (monto) => {
  if (!monto && monto !== 0) return '';
  return new Intl.NumberFormat('es-CL', {
    style: 'currency',
    currency: 'CLP',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(monto);
};

const RutasTable = ({
  data,
  loading,
  total,
  pageSize,
  pageNumber,
  onPageChange,
  onPageSizeChange,
  onRowClick,
  searchValue,
  onSearch,
  empresas,
  empresaId,
  onEmpresaChange,
  tmsList,
  tmsId,
  onTmsChange
}) => {
  // Columnas de la tabla
  const columns = [
    { Header: 'Ruta', accessor: row => row.ruta },
    { Header: 'UsuarioSpread', accessor: row => row.usuarioSpread || row.conductor?.usuarioSpread || '' },
    { Header: 'Empresa', accessor: row => row.empresa?.sigla || '' },
    { Header: 'Fecha', accessor: row => row.fecha },
    // { Header: 'Estado', accessor: row => row.estado_actual?.estado || '' },
    { Header: 'Origen', accessor: row => row.tms?.tms || '' },
    { Header: 'NS', accessor: row => typeof row.ns === 'number' ? `${row.ns.toFixed(2)}%` : '' },
    { Header: 'NsConsolidado', accessor: row => typeof row.ns_consolidado === 'number' ? `${row.ns_consolidado.toFixed(2)}%` : '' },
    { Header: 'Patente', accessor: row => row.ppu_vehiculo },
    { Header: 'A pagar', accessor: row => {
        // Si tiene precio fijo, usar precio_fijo, sino usar precio_guias
        const precio = row.tipo_precio === 'fijo' && row.precio_fijo !== null 
            ? row.precio_fijo 
            : row.estadisticas?.precio_total || 0;
        return formatMonto(precio);
    }},
  ];

  // Renderizar cabecera
  const renderHeader = () => (
    <tr>
      {columns.map((col, idx) => (
        <th key={idx}>{col.Header}</th>
      ))}
    </tr>
  );

  // Renderizar filas
  const renderRows = () => (
    data.map((row, idx) => (
      <tr key={row.id || idx} style={{ cursor: 'pointer' }} onClick={() => onRowClick(row)}>
        {columns.map((col, cidx) => (
          <td key={cidx}>{col.accessor(row)}</td>
        ))}
      </tr>
    ))
  );

  // Paginación estilo DirectorioTable
  const pageIndex = Math.max(0, (pageNumber || 1) - 1);
  const pageCount = Math.max(1, Math.ceil(total / pageSize));

  const renderPagination = () => (
    <div className="d-flex justify-content-between align-items-center mt-2">
      <div className="d-flex align-items-center gap-2">
        <button className="btn btn-outline-primary btn-sm" onClick={() => onPageChange(1)} disabled={pageIndex === 0}>{'<<'}</button>
        <button className="btn btn-outline-primary btn-sm" onClick={() => onPageChange(pageIndex)} disabled={pageIndex === 0}>{'<'}</button>
        <span style={{ minWidth: 90, textAlign: 'center' }}>
          Página <strong>{pageIndex + 1}</strong> de <strong>{pageCount}</strong>
        </span>
        <button className="btn btn-outline-primary btn-sm" onClick={() => onPageChange(pageIndex + 2)} disabled={pageIndex + 1 >= pageCount}>{'>'}</button>
        <button className="btn btn-outline-primary btn-sm" onClick={() => onPageChange(pageCount)} disabled={pageIndex + 1 >= pageCount}>{'>>'}</button>
      </div>
      <div className="d-flex align-items-center gap-2">
        <span className="me-2">Total rutas: <strong>{total}</strong></span>
        <select value={pageSize} onChange={e => onPageSizeChange(Number(e.target.value))} className="form-select form-select-sm" style={{ width: 'auto', display: 'inline-block' }}>
          {[10, 20, 50, 100].map(size => (
            <option key={size} value={size}>Mostrar {size}</option>
          ))}
        </select>
      </div>
    </div>
  );

  return (
    <div>
      {/* Filtros */}
      <Row className="mb-3">
        <Col md={3}>
          <Form.Group controlId="empresaSelect">
            <Form.Label>Empresa</Form.Label>
            <Form.Select
              value={empresaId || ''}
              onChange={e => onEmpresaChange(e.target.value)}
              size="sm"
            >
              <option value="">Todas</option>
              {empresas.map(emp => (
                <option key={emp.id} value={emp.id}>{emp.sigla}</option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group controlId="tmsSelect">
            <Form.Label>Origen</Form.Label>
            <Form.Select
              value={tmsId || ''}
              onChange={e => onTmsChange(e.target.value)}
              size="sm"
            >
              <option value="">Todos</option>
              {tmsList.map(tms => (
                <option key={tms.id} value={tms.id}>{tms.tms}</option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group controlId="searchInput">
            <Form.Label>Búsqueda Global</Form.Label>
            <InputGroup>
              <Form.Control
                type="text"
                placeholder="Buscar... (mínimo 5 caracteres para live search)"
                value={searchValue}
                onChange={e => onSearch(e.target.value)}
                size="sm"
              />
              {loading && (
                <InputGroup.Text>
                  <Spinner animation="border" size="sm" />
                </InputGroup.Text>
              )}
              {searchValue && (
                <Button variant="outline-secondary" size="sm" onClick={() => onSearch('')}>Limpiar</Button>
              )}
            </InputGroup>
          </Form.Group>
        </Col>
      </Row>

      {/* Tabla */}
      <div style={{ overflowX: 'auto', position: 'relative' }}>
        {loading && data.length === 0 ? (
          <div className="d-flex justify-content-center align-items-center" style={{ height: '120px' }}>
            <Spinner animation="border" />
          </div>
        ) : (
          <>
            {/* Overlay de loading cuando hay datos previos */}
            {loading && data.length > 0 && (
              <div 
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: 'rgba(255, 255, 255, 0.8)',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  zIndex: 10
                }}
              >
                <Spinner animation="border" />
              </div>
            )}
            <table className="table table-hover" style={{ minWidth: '900px' }}>
              <thead>{renderHeader()}</thead>
              <tbody>
                {!loading && data.length > 0 ? renderRows() : (
                  <tr><td colSpan={columns.length} className="text-center">No se encontraron rutas</td></tr>
                )}
              </tbody>
            </table>
          </>
        )}
      </div>
      {renderPagination()}
    </div>
  );
};

RutasTable.propTypes = {
  data: PropTypes.array.isRequired,
  loading: PropTypes.bool,
  total: PropTypes.number,
  pageSize: PropTypes.number,
  pageNumber: PropTypes.number,
  onPageChange: PropTypes.func,
  onPageSizeChange: PropTypes.func,
  onRowClick: PropTypes.func,
  searchValue: PropTypes.string,
  onSearch: PropTypes.func,
  empresas: PropTypes.array,
  empresaId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onEmpresaChange: PropTypes.func,
  tmsList: PropTypes.array,
  tmsId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onTmsChange: PropTypes.func
};

export default RutasTable;
